/**
 * Pure presentation for a single editable extraction line-item row.
 *
 * Owns no logic — receives pre-bound handlers from
 * {@link LineItemRowContainer} and composes the conflict /
 * recently-modified / inline-editing presentation states by delegating
 * to {@link LineItemInlineEditRow} and {@link LineItemDisplayRow}.
 *
 * Hover affordances (pencil + trash) are revealed on web via
 * `group-hover` and stay always-visible on native because there is no
 * hover surface on touch — both interaction modes route through the
 * same pre-bound `onPressEdit` / `onPressDelete` callbacks.
 *
 * When `readOnly` is `true`, the row renders without any edit
 * affordances — plain text (non-pressable), no pencil/trash buttons,
 * no inline edit input. Conflict + recently-modified styling is
 * preserved so the data stays legible in saved extractions.
 * @module features/contracts/components/LineItemRow/LineItemRowView
 */

import React, { memo } from "react";

import LineItemDisplayRow from "@/features/contracts/components/molecules/LineItemDisplayRow";
import LineItemInlineEditRow from "@/features/contracts/components/molecules/LineItemInlineEditRow";

import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";

/** Props for {@link LineItemRowView}. */
export interface LineItemRowViewProps {
  /** Category key the row belongs to. */
  readonly category: string;
  /** Zero-based index within the category's items array. */
  readonly index: number;
  /** Display text of the row when not editing. */
  readonly text: string;
  /** Whether the AI flagged this item as a conflict. */
  readonly isConflict: boolean;
  /** Whether this row was edited or added since the last refetch. */
  readonly isRecentlyModified: boolean;
  /** Whether the row is currently in inline-edit mode. */
  readonly isBeingEdited: boolean;
  /** Working draft text shown in the inline edit input (only used when `isBeingEdited`). */
  readonly draftText: string;
  /** Whether the edit mutation is currently in flight. */
  readonly isEditingMutation: boolean;
  /** Per-row edit error message to render inline, or null. */
  readonly editErrorMessage: string | null;
  /** Per-row delete error message to render inline, or null. */
  readonly deleteErrorMessage: string | null;
  /** Pre-bound handler for entering edit mode (text press or pencil click). */
  readonly onPressEdit: () => void;
  /** Forwards inline-edit input changes. */
  readonly onChangeEditDraft: (text: string) => void;
  /** Forwards inline-edit save (checkmark). */
  readonly onSaveEdit: () => void;
  /** Forwards inline-edit cancel (X). */
  readonly onCancelEdit: () => void;
  /** Pre-bound handler for opening the delete confirmation dialog. */
  readonly onPressDelete: () => void;
  /**
   * When `true`, renders the row without edit affordances: the text is
   * plain (non-pressable), the pencil/trash buttons are hidden, and
   * the inline edit input is suppressed even if `isBeingEdited` is
   * somehow set. Conflict + recently-modified styling is preserved.
   */
  readonly readOnly?: boolean;
}

/**
 * Renders a single editable line-item row.
 * @param props - Component props
 * @param props.category - Category key the row belongs to.
 * @param props.index - Zero-based index within the category's items array.
 * @param props.text - Display text of the row when not editing.
 * @param props.isConflict - Whether the AI flagged this item as a conflict.
 * @param props.isRecentlyModified - Whether this row was edited or added since the last refetch.
 * @param props.isBeingEdited - Whether the row is currently in inline-edit mode.
 * @param props.draftText - Working draft text shown in the inline edit input (only used when `isBeingEdited`).
 * @param props.isEditingMutation - Whether the edit mutation is currently in flight.
 * @param props.editErrorMessage - Per-row edit error message to render inline, or null.
 * @param props.deleteErrorMessage - Per-row delete error message to render inline, or null.
 * @param props.onPressEdit - Pre-bound handler for entering edit mode (text press or pencil click).
 * @param props.onChangeEditDraft - Forwards inline-edit input changes.
 * @param props.onSaveEdit - Forwards inline-edit save (checkmark).
 * @param props.onCancelEdit - Forwards inline-edit cancel (X).
 * @param props.onPressDelete - Pre-bound handler for opening the delete confirmation dialog.
 * @param props.readOnly - When `true`, renders the row without edit affordances: the text is plain (non-pressable), the pencil/trash buttons are hidden, and the inline edit input is suppressed even if `isBeingEdited` is somehow set. Conflict + recently-modified styling is preserved.
 * @returns The rendered row
 */
const LineItemRowView = ({
  category,
  index,
  text,
  isConflict,
  isRecentlyModified,
  isBeingEdited,
  draftText,
  isEditingMutation,
  editErrorMessage,
  deleteErrorMessage,
  onPressEdit,
  onChangeEditDraft,
  onSaveEdit,
  onCancelEdit,
  onPressDelete,
  readOnly = false,
}: LineItemRowViewProps) => (
  <Box testID={`line-item-${category}-${index}`}>
    <Stack className="group gap-1">
      {isBeingEdited && !readOnly ? (
        <LineItemInlineEditRow
          category={category}
          index={index}
          draftText={draftText}
          isEditingMutation={isEditingMutation}
          onChangeEditDraft={onChangeEditDraft}
          onSaveEdit={onSaveEdit}
          onCancelEdit={onCancelEdit}
        />
      ) : (
        <LineItemDisplayRow
          category={category}
          index={index}
          text={text}
          isConflict={isConflict}
          isRecentlyModified={isRecentlyModified}
          readOnly={readOnly}
          onPressEdit={onPressEdit}
          onPressDelete={onPressDelete}
        />
      )}
      {editErrorMessage !== null ? (
        <Box testID={`line-item-edit-error-${category}-${index}`}>
          <Text
            variant="caption"
            className="pl-2 text-needs-semantic-error-600"
          >
            {editErrorMessage}
          </Text>
        </Box>
      ) : null}
      {deleteErrorMessage !== null ? (
        <Box testID={`line-item-delete-error-${category}-${index}`}>
          <Text
            variant="caption"
            className="pl-2 text-needs-semantic-error-600"
          >
            {deleteErrorMessage}
          </Text>
        </Box>
      ) : null}
    </Stack>
  </Box>
);

LineItemRowView.displayName = "LineItemRowView";

export default memo(LineItemRowView);
